import React from "react";
import PropTypes from "prop-types";

import { makeStyles } from "@material-ui/core";
import pluralize from "pluralize";

import Money from "components/Money";
import Table from "components/Table";

import { DeltaLabel, Seats } from "./components";

const useStyles = makeStyles({
  tableContainer: {},
  headerCell: {
    fontSize: "0.875rem",
    background: "#FFFFFF",
  },
});

const tableColumns = [
  { id: "name", style: { width: "200px" } },
  { id: "seats", label: "Used Seats" },
  { id: "unitPrice", label: "" },
  { id: "totalPrice", label: "Total" },
];

const toTableRow = (subscription) => {
  if (!subscription) {
    return [{}];
  }

  return [
    {
      ...subscription,
      name: subscription.name,
      seats: <Seats {...subscription} />,
      unitPrice: (
        <Money currency={subscription.currency}>{subscription.unitPrice}</Money>
      ),
      totalPrice: (
        <span>
          {subscription.nextBillingPrice ? (
            <React.Fragment>
              <Money currency={subscription.currency}>
                {subscription.nextBillingPrice}
              </Money>
              {` (billed ${subscription.billingPeriod})`}
              {subscription.deltaPrice ? (
                <div>
                  <DeltaLabel deltaQty={subscription.deltaPrice}>
                    <Money currency={subscription.currency}>
                      {subscription.deltaPrice}
                    </Money>
                  </DeltaLabel>
                </div>
              ) : (
                ""
              )}
            </React.Fragment>
          ) : (
            "N/A"
          )}
        </span>
      ),
    },
  ];
};

function SubscriptionInformationTable({
  loading,
  title,
  emptySubscriptionMsg,
  subscription,
}) {
  const classes = useStyles();

  const prepareColumns = (planMultiple) => {
    return tableColumns.map(({ id, label, ...rest }) => {
      if (id === "unitPrice") {
        return {
          id: id,
          label: `Price per ${planMultiple} ${pluralize("Seat", planMultiple)}`,
        };
      }
      return { id, label, ...rest };
    });
  };

  return (
    <React.Fragment>
      {title && <h3>{title}</h3>}

      {!subscription && !loading && emptySubscriptionMsg ? (
        emptySubscriptionMsg
      ) : (
        <Table
          className={classes.tableContainer}
          data-test={`${title.toLowerCase().replace(/\s/g, "-")}-table`}
        >
          <Table.Content>
            <Table.Header
              columns={prepareColumns(subscription?.planMultiple)}
              className={classes.headerCell}
              align="center"
            />
            <Table.Body
              isLoading={loading}
              columns={prepareColumns(subscription?.planMultiple)}
              align="center"
              data={toTableRow(subscription)}
            />
          </Table.Content>
        </Table>
      )}
    </React.Fragment>
  );
}

SubscriptionInformationTable.propTypes = {
  loading: PropTypes.bool,
  title: PropTypes.string,
  emptySubscriptionMsg: PropTypes.node,
  subscription: PropTypes.shape({
    name: PropTypes.string.isRequired,
    usedSeats: PropTypes.number.isRequired,
    deltaUsers: PropTypes.number,
    deltaSeats: PropTypes.number,
    deltaPrice: PropTypes.string,
    planMultiple: PropTypes.number,
    unitPrice: PropTypes.string,
    numSeats: PropTypes.number,
    nextBillingPrice: PropTypes.string,
    currency: PropTypes.string,
    billingPeriod: PropTypes.string,
    nextBillingDate: PropTypes.instanceOf(Date),
  }),
};

SubscriptionInformationTable.defaultProps = {
  loading: false,
  title: "",
  emptySubscriptionMsg: null,
  subscription: null,
};

export default SubscriptionInformationTable;
